import { any, async, fromJSX, loadingAsyncState, nothing, unloadedAsyncState } from "widgets-for-react";
import { allLocationsId, fetchDefaultFacets, fetchSearchResponse } from "../../../backendApi/CollectionApi";
import { hasValue, previousOrNull } from "../../../utils/asyncState";
import { parseParamsFromUrl } from "../../../utils/browserQueryParams";
import { naiveEqual } from "../../../utils/object";
import { useEffect } from "../../../utils/useEffect";
import { defaultFilters, emptyFilters, templateFilterSettings } from "./CollectionOverviewFilter";
import { CollectionOverviewLayout, collectionResultsId } from "./CollectionOverviewLayout";
import { defaultPagination } from "./CollectionOverviewState";
import { searchParams } from "./CollectionOverviewUtils";
export const CollectionOverviewWidget = (props)=>{
    var _props_response_value_CurrentParams;
    const filterSettings = parseParamsFromUrl(defaultFilters(props.preferredBranchId), templateFilterSettings);
    const paginationParams = parseParamsFromUrl(defaultPagination);
    const collectionSearchParams = {
        ...filterSettings,
        ...paginationParams
    };
    const normalizeLocation = (location)=>location == null || location === '' || location === allLocationsId ? undefined : location;
    const normalizedCollectionSearchParams = {
        ...collectionSearchParams,
        location: normalizeLocation(collectionSearchParams.location)
    };
    const currentSearchParams = props.response.kind == 'loaded' ? {
        ...props.response.value.CurrentParams,
        location: normalizeLocation((_props_response_value_CurrentParams = props.response.value.CurrentParams) === null || _props_response_value_CurrentParams === void 0 ? void 0 : _props_response_value_CurrentParams.location)
    } : normalizedCollectionSearchParams;
    const shouldUpdate = filterReady(collectionSearchParams) && (props.response.kind == 'unloaded' || props.response.kind == 'loaded' && !naiveEqual(currentSearchParams, normalizedCollectionSearchParams));
    const updateParams = function(newSettings) {
        let addHistoryEntry = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : false;
        const historyMethod = addHistoryEntry ? props.history.push : props.history.replace;
        const updatedFilterSettings = {
            ...filterSettings,
            ...newSettings
        };
        historyMethod(`${props.config.route}${searchParams(updatedFilterSettings)}`);
    };
    const updateLocation = (location)=>{
        const from = filterSettings.location;
        const to = location;
        if (from === to) return;
        if (from !== allLocationsId && to !== allLocationsId) return updateParams({
            location
        });
        // Toggling location on/off: "availableAnywhere" and "availableHere"/"availableElsewhere" are
        // semantically equivalent per mode, so translate whichever of them is selected. Leave the rest alone.
        const status = new Set(filterSettings.status);
        if (from === allLocationsId && status.delete('availableAnywhere')) {
            status.add('availableHere');
            status.add('availableElsewhere');
        } else if (to === allLocationsId) {
            const hadHere = status.delete('availableHere');
            const hadElsewhere = status.delete('availableElsewhere');
            if (hadHere || hadElsewhere) status.add('availableAnywhere');
        }
        updateParams({
            location,
            status: [
                ...status
            ]
        });
    };
    return any({
        key: 'CollectionOverviewResults'
    })([
        // Render Layout
        fromJSX((setState)=>{
            return CollectionOverviewLayout({
                ...props,
                filterSettings,
                filterUpdaters: {
                    updateQuery: (query)=>updateParams({
                            query
                        }, true),
                    updateMedium: (medium)=>updateParams({
                            medium
                        }),
                    updateGenre: (genre)=>updateParams({
                            genre
                        }),
                    updateLocation,
                    updateStatus: (status)=>updateParams({
                            status
                        }),
                    updateAgeRange: (ageFrom, ageTo)=>updateParams({
                            ageFrom,
                            ageTo
                        })
                },
                toggleFilterExpanded: ()=>setState((s0)=>({
                            ...s0,
                            filterExpanded: !s0.filterExpanded
                        })),
                facets: hasValue(props.response) ? props.response.value.Facets : hasValue(props.defaultFacets) ? props.defaultFacets.value : 'unknown'
            });
        }),
        // When search params change, update the async search response
        useEffect((setState)=>shouldUpdate ? setState((s0)=>({
                    ...s0,
                    response: loadingAsyncState(fetchSearchResponse(collectionSearchParams), previousOrNull(s0.response))
                })) : undefined, [
            collectionSearchParams
        ], {
            key: 'CollectionSearchResponseUpdater'
        }),
        // Manage async lifecycle of search response
        async(nothing, {
            key: 'CollectionSearchResponseLoader'
        })(props.response).map((resultUpdater)=>(s0)=>{
                // Update actual response
                const response = resultUpdater(s0.response);
                // Scroll into view when new results are loaded
                if (s0.response.kind != 'loaded' && response.kind === 'loaded') {
                    setTimeout(()=>scrollResultsIntoView(), 10); // Delay to allow rendering first
                }
                return {
                    ...s0,
                    response
                };
            }),
        // Manage async lifecycle of default facets
        async(nothing, {
            key: 'CollectionDefaultFacetsLoader'
        })(props.defaultFacets).map((resultUpdater)=>(s0)=>({
                    ...s0,
                    defaultFacets: resultUpdater(s0.defaultFacets)
                }))
    ]);
};
const hasAnyActiveFilter = (settings)=>Object.values(settings).some((value)=>value.length > 0);
/**
 * Checks if any filter in the given settings is active.
 * @param settings FilterSettings where each property must have a length property.
 * @returns True if any property of `settings` has a length greater than 0.
 */ export const filterReady = (settings)=>{
    const { ageFrom, ageTo, ...settingsWithLength } = settings;
    return hasAnyActiveFilter(settingsWithLength) || ageFrom != null && ageTo != null;
};
function scrollResultsIntoView() {
    const resultsElement = document.getElementById(collectionResultsId);
    const resultsViewportPosition = resultsElement.getBoundingClientRect().top;
    const resultsPagePosition = resultsViewportPosition + window.scrollY;
    const offset = document.querySelector('header').clientHeight;
    const newPosition = resultsPagePosition - offset;
    if (window.scrollY > newPosition) window.scrollTo({
        top: newPosition,
        behavior: 'smooth'
    });
}
export const initialCollectionOverviewState = ()=>{
    let filterSettings = emptyFilters;
    let pagination = defaultPagination;
    let response = unloadedAsyncState();
    return {
        defaultFacets: loadingAsyncState(fetchDefaultFacets),
        filterSettings,
        filterExpanded: false,
        response,
        pagination
    };
};
